import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';

import type { MergeFieldItem } from './merge-field-suggestion';

interface MergeFieldPickerProps {
  items: MergeFieldItem[];
  command: (item: MergeFieldItem) => void;
}

interface MergeFieldPickerRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

export const MergeFieldPicker = forwardRef<
  MergeFieldPickerRef,
  MergeFieldPickerProps
>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex(
          (current) =>
            (current + props.items.length - 1) % props.items.length,
        );
        return true;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex((current) => (current + 1) % props.items.length);
        return true;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        const selectedItem = props.items[selectedIndex];
        if (selectedItem) {
          props.command(selectedItem);
        }
        return true;
      }

      return false;
    },
  }));

  // Group items by category
  const grouped = props.items.reduce<Record<string, MergeFieldItem[]>>(
    (acc, item) => {
      if (!acc[item.category]) acc[item.category] = [];
      acc[item.category].push(item);
      return acc;
    },
    {},
  );

  // Compute a flat index offset for each category group
  const groupEntries = Object.entries(grouped);
  const groupOffsets = new Map<string, number>();
  let offset = 0;
  groupEntries.forEach(([category, items]) => {
    groupOffsets.set(category, offset);
    offset += items.length;
  });

  return (
    <Command className="z-[100] overflow-hidden rounded-lg border bg-white shadow-md dark:bg-zinc-900">
      <CommandList className="max-h-[250px] overflow-y-auto py-1">
        {groupEntries.length === 0 && (
          <div className="p-3 text-sm font-medium text-zinc-500">
            No variables found
          </div>
        )}
        {groupEntries.map(([category, items]) => (
          <CommandGroup
            key={category}
            heading={category}
            className="px-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-zinc-500"
          >
            {items.map((item, idx) => {
              const thisIndex = (groupOffsets.get(category) ?? 0) + idx;
              return (
                <CommandItem
                  key={item.key}
                  value={item.key}
                  onSelect={() => props.command(item)}
                  onMouseEnter={() => setSelectedIndex(thisIndex)}
                  className={cn(
                    'cursor-pointer px-2 py-1.5 text-sm font-medium transition-colors',
                    thisIndex === selectedIndex &&
                      'bg-violet-100 text-violet-900 dark:bg-violet-500/15 dark:text-violet-200',
                  )}
                >
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center justify-between gap-3">
                      <span>{item.label}</span>
                      <span className="font-mono text-[10px] text-zinc-500">
                        {item.key}
                      </span>
                    </div>
                    {item.sample && (
                      <span className="truncate text-[11px] font-medium text-zinc-500">
                        {item.sample}
                      </span>
                    )}
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}
      </CommandList>
    </Command>
  );
});

MergeFieldPicker.displayName = 'MergeFieldPicker';
